import React, { useEffect, useRef, useState } from 'react';
import { IoAlertCircleOutline, IoCloseOutline, IoFolderOutline, IoSearchOutline } from 'react-icons/io5';
import { FileKindIcon } from '../atlas/viewer/fileIcons';
import FileList, { VersionBadge } from './FileList';
import { SOURCE_GROUP_CHIPS, applyOverrides, folderBadge, formatDate, pluralize, timeAgo } from './fileLibrary';

const SEARCH_DEBOUNCE_MS = 300;
const RECENT_LIMIT = 30;

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [delay, value]);
  return debounced;
}

/** The latest files as one lean list: name, where it came from, when it last changed. */
function RecentList({ client, refreshKey, overrides, onOpenFile, onFreshFiles, onShowAll }) {
  const [state, setState] = useState({ status: 'loading', files: [], error: null });
  const [attempt, setAttempt] = useState(0);
  const onFreshFilesRef = useRef(onFreshFiles);
  onFreshFilesRef.current = onFreshFiles;

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: prev.files.length ? 'refreshing' : 'loading', error: null }));
    client.listFiles({ sort: 'recent', limit: RECENT_LIMIT })
      .then((result) => {
        if (cancelled) return;
        const files = result.files.slice(0, RECENT_LIMIT);
        setState({ status: 'ready', files, error: null });
        if (result.fetchSeq != null) onFreshFilesRef.current?.(files.map((f) => f.file_id), result.fetchSeq);
      })
      .catch((err) => { if (!cancelled) setState({ status: 'error', files: [], error: err?.message || 'Recent files could not be loaded.' }); });
    return () => { cancelled = true; };
  }, [attempt, client, refreshKey]);

  const files = applyOverrides(state.files, overrides);

  return (
    <section className="fl-home-list" aria-label="Recent files">
      <div className="fl-home-list-head" aria-hidden="true">
        <span>Name</span>
        <span className="fl-home-source">Source</span>
        <span>Last activity</span>
      </div>
      {state.status === 'loading' ? (
        <div aria-busy="true">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="fl-home-row fl-skeleton-row" />)}</div>
      ) : state.status === 'error' ? (
        <div className="fl-inline-error" role="alert">
          <IoAlertCircleOutline size={16} aria-hidden="true" />
          <span>{state.error}</span>
          <button type="button" className="fl-link-btn" onClick={() => setAttempt((n) => n + 1)}>Retry</button>
        </div>
      ) : files.length === 0 ? (
        <div className="fl-empty fl-empty-compact">
          <strong>No files yet</strong>
          <span>Files you upload in chat, files Atlas creates, email attachments and research downloads all land here.</span>
        </div>
      ) : (
        <>
          <ul className="fl-home-rows">
            {files.map((file) => {
              const at = file.updated_at || file.created_at;
              return (
                <li key={file.file_id}>
                  <button type="button" className="fl-home-row" onClick={() => onOpenFile(file)} title={file.filename}>
                    <span className="fl-home-name">
                      <span className={`fl-home-icon fl-kind-${file.kind}`}><FileKindIcon file={file} size={18} /></span>
                      <span className="fl-home-filename">{file.filename}</span>
                      <VersionBadge file={file} />
                    </span>
                    <span className="fl-home-source">{SOURCE_GROUP_CHIPS[file.source_group] || '—'}</span>
                    <span className="fl-home-time" title={formatDate(at, { withTime: true })}>{timeAgo(at)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {files.length >= RECENT_LIMIT ? (
            <button type="button" className="fl-link-btn fl-home-more" onClick={onShowAll}>View all files</button>
          ) : null}
        </>
      )}
    </section>
  );
}

export function FolderCards({ folders, folderIndex, onOpenFolder, emptyText }) {
  if (!folders.length) return emptyText ? <p className="fl-muted">{emptyText}</p> : null;
  return (
    <ul className="fl-folder-cards">
      {folders.map((folder) => {
        const badge = folderBadge(folder);
        const hint = folderIndex.hintOf(folder.folder_id);
        return (
          <li key={folder.folder_id}>
            <button type="button" className="fl-folder-card" onClick={() => onOpenFolder(folder.folder_id)}>
              <span className="fl-folder-card-top">
                <IoFolderOutline size={18} className="fl-folder-glyph" aria-hidden="true" />
                <span className="fl-folder-card-name">{folder.name}</span>
                {badge ? <span className={`fl-badge fl-badge-${badge.toLowerCase()}`}>{badge}</span> : null}
              </span>
              <span className="fl-folder-card-count">{pluralize(Number(folder.file_count) || 0, 'file')}{hint ? ` · ${hint}` : ''}</span>
              {folder.description ? <span className="fl-folder-card-desc">{folder.description}</span> : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default function LibraryHome({
  client,
  refreshKey,
  overrides,
  listProps,
  onOpenFile,
  onSelectView,
}) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS);
  const searching = Boolean(debouncedQuery);

  return (
    <div className="fl-home">
      <div className="fl-search">
        <IoSearchOutline size={17} aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search files, summaries and tags"
          aria-label="Search files"
        />
        {query ? (
          <button type="button" className="fl-icon-btn fl-icon-btn-sm" onClick={() => setQuery('')} aria-label="Clear search">
            <IoCloseOutline size={16} />
          </button>
        ) : null}
      </div>

      {searching ? (
        <FileList
          {...listProps}
          client={client}
          params={{ q: debouncedQuery }}
          refreshKey={refreshKey}
          overrides={overrides}
          title={`Results for “${debouncedQuery}”`}
          emptyTitle="No files match"
          emptyHint="Search looks at file names, summaries, tags and the people, places and companies in each file."
        />
      ) : (
        <RecentList
          client={client}
          refreshKey={refreshKey}
          overrides={overrides}
          onOpenFile={onOpenFile}
          onFreshFiles={listProps?.onFreshFiles}
          onShowAll={() => onSelectView({ id: 'all' })}
        />
      )}
    </div>
  );
}
